import { CalendarDialog } from "@/components/CalendarDialog";
import { motion } from "framer-motion";
import { Instagram, Facebook, MapPin, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import fullscaleLogoWhite from "@/assets/fullscale-logo-white.png";

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Contact", href: "/contact" },
];

const serviceLinks = [
  { label: "SMART Websites", href: "/services/websites" },
  { label: "GBP & Local SEO", href: "/services/gbp-seo" },
  { label: "Reputation & Reviews", href: "/services/reputation" },
  { label: "Lead Generation Ads", href: "/services/lead-generation" },
  { label: "Automation & AI", href: "/services/automation" },
  { label: "Reactivation Campaigns", href: "/services/reactivation" },
];

const serviceAreas = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Newcastle", "Canberra"];
const tradesIndustries = ["Electricians", "Plumbers", "Builders", "Roofers", "HVAC", "Landscapers", "Painters", "Pool Builders"];
const serviceIndustries = ["Cleaning Companies", "Hair Salons", "Pest Control", "Gyms & Fitness", "Veterinary Clinics", "Beauty Services", "Moving Services"];

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[hsl(160_10%_6%)] relative" role="contentinfo" aria-label="Site footer">
      {/* Top decorative separator */}
      <div className="h-10 sm:h-12 bg-gradient-to-b from-background-dark to-[hsl(160_10%_6%)] relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      </div>

      {/* Footer CTA */}
      <div className="py-16 sm:py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(160_10%_8%)] to-[hsl(160_10%_5%)]" />
        <div className="absolute inset-0 grid-pattern-dark opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[800px] h-[240px] sm:h-[400px] bg-primary/[0.07] rounded-full blur-[120px] sm:blur-[160px]" />

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="container-wide text-center relative px-4"
        >
          <div className="section-badge-dark mb-5">
            <span>Let's Talk</span>
          </div>
          <h3 className="text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-foreground-dark leading-[1.05] tracking-[-0.03em] mb-4 max-w-2xl mx-auto">
            Ready to grow your trade or service business?
          </h3>
          <p className="text-foreground-dark/65 mb-8 max-w-xl mx-auto text-base sm:text-[1.0625rem] leading-[1.65]">
            Book a free strategy call and discover how our digital growth system brings in more leads, reviews and high-value jobs.
          </p>
          <CalendarDialog
            buttonVariant="dark-primary"
            buttonSize="default"
            buttonClassName="h-12 sm:h-14 px-8 sm:px-10 text-sm sm:text-base font-bold rounded-xl shadow-[0_8px_32px_-8px_hsl(98_58%_38%/0.5)]"
          />
        </motion.div>
      </div>

      {/* Separator */}
      <div className="relative h-px">
        <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-foreground-dark/20 to-transparent" />
      </div>

      {/* Main Footer */}
      <div className="py-14 sm:py-16 bg-[hsl(160_10%_4%)]">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
            {/* Brand */}
            <div className="md:col-span-5 lg:col-span-4">
              <Link to="/" className="inline-block mb-5" aria-label="FullScale Marketing - Back to home">
                <img
                  src={fullscaleLogoWhite}
                  alt="FullScale Marketing"
                  className="h-8 sm:h-10 w-auto"
                  loading="lazy"
                />
              </Link>
              <p className="text-foreground-dark/60 max-w-md text-[15px] leading-relaxed mb-4">
                Australia's specialist digital marketing agency for trades and local service businesses. We help you get found on Google, build trust through reviews, and win more high-value jobs and bookings.
              </p>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 mb-4">
                <span className="text-primary text-xs font-semibold">Digital Marketing Agency</span>
                <span className="text-foreground-dark/40 text-xs">•</span>
                <span className="text-foreground-dark/60 text-xs">Trades & Local Service Specialists</span>
              </div>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-2 lg:col-span-2">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">Quick Links</h4>
              <nav className="space-y-3" aria-label="Quick links">
                {quickLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-foreground-dark/60 hover:text-primary transition-colors duration-300 text-[15px]"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Services */}
            <div className="md:col-span-2 lg:col-span-2">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">Services</h4>
              <nav className="space-y-3" aria-label="Service links">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-foreground-dark/60 hover:text-primary transition-colors duration-300 text-[15px]"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Service Areas + Connect */}
            <div className="md:col-span-3 lg:col-span-4">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">Service Areas</h4>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {serviceAreas.map((area) => (
                  <span key={area} className="text-foreground-dark/50 text-[13px] bg-foreground-dark/5 px-2 py-1 rounded">
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-foreground-dark/40 text-xs mb-6">+ All regional areas Australia-wide</p>

              <h4 className="font-semibold text-foreground-dark mb-3 text-[15px]">Connect</h4>
              <div className="flex items-center gap-4 mb-3">
                <a href="https://www.instagram.com/fullscale_marketing/" target="_blank" rel="noopener noreferrer" className="text-foreground-dark/60 hover:text-foreground-dark transition-colors" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/p/FullScale-Marketing-61562089223781/" target="_blank" rel="noopener noreferrer" className="text-foreground-dark/60 hover:text-foreground-dark transition-colors" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
              <div className="space-y-2 text-foreground-dark/50 text-[13px]">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Australia-wide</span>
                </div>
                <a href="mailto:info@fullscalemarketing.com.au" className="flex items-center gap-2 hover:text-foreground-dark transition-colors">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>info@fullscalemarketing.com.au</span>
                </a>
              </div>
            </div>
          </div>

          {/* Industries */}
          <div className="mt-10 pt-8 border-t border-foreground-dark/10">
            <h4 className="font-semibold text-foreground-dark mb-5 text-sm text-center">Industries We Serve</h4>
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="text-center sm:text-left">
                <p className="text-primary text-[11px] font-bold uppercase tracking-wider mb-2">Trades & Construction</p>
                <p className="text-foreground-dark/50 text-xs leading-relaxed">
                  {tradesIndustries.join(" • ")}
                </p>
              </div>
              <div className="text-center sm:text-left">
                <p className="text-primary text-[11px] font-bold uppercase tracking-wider mb-2">Service Businesses</p>
                <p className="text-foreground-dark/50 text-xs leading-relaxed">
                  {serviceIndustries.join(" • ")}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-10 mt-8 border-t border-foreground-dark/10 flex flex-col items-center gap-4">
            <p className="text-sm text-foreground-dark/50 text-center">
              © {currentYear} FullScale Marketing. All rights reserved.
            </p>
            <div className="flex items-center gap-8 text-sm text-foreground-dark/50">
              <a href="#" className="hover:text-foreground-dark transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-foreground-dark transition-colors">Terms of Service</a>
            </div>
          </div>

          {/* SEO Footer */}
          <div className="mt-8 text-center">
            <p className="text-foreground-dark/30 text-xs leading-relaxed max-w-3xl mx-auto">
              FullScale Marketing is Australia's specialist digital marketing agency for trades and local service businesses — including electricians, plumbers, builders, roofers, HVAC, cleaners, gyms, salons, pest control and more. We provide Local SEO, Google Ads, Facebook Ads, websites and Google review systems across Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Newcastle, Canberra and all regional areas.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
